import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { i18n } from '../i18n';
import {
  formatMessageTime,
  shouldShowMessageTime,
} from '../lib/formatChatTime';
import { tokens } from '../theme';
import { capitalizeErrorMessage } from '../lib/errors';
import { formatFileSize } from '../lib/attachments';
import type { ChatMessage, SentAttachment } from '../types/chat';
import ConversationDivider from './ConversationDivider';
import MessageTimestamp from './MessageTimestamp';
import TypingIndicator from './TypingIndicator';

const styles = {
  bubbleUser: {
    alignSelf: 'flex-end',
    maxWidth: '85%',
    background: tokens.gradient.accent,
    color: tokens.text.onBrand,
    padding: '10px 14px',
    borderRadius: `${tokens.radius.lg} ${tokens.radius.lg} ${tokens.radius.bubbleTail} ${tokens.radius.lg}`,
    fontSize: tokens.font.size.md,
    lineHeight: tokens.font.lineHeight.body,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  bubbleAvatar: {
    alignSelf: 'flex-start',
    maxWidth: '85%',
    background: tokens.surface.elevated,
    color: tokens.text.secondary,
    padding: '10px 14px',
    borderRadius: `${tokens.radius.lg} ${tokens.radius.lg} ${tokens.radius.lg} ${tokens.radius.bubbleTail}`,
    fontSize: tokens.font.size.md,
    lineHeight: tokens.font.lineHeight.body,
    border: `1px solid ${tokens.border.default}`,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  bubbleSystem: {
    alignSelf: 'center',
    maxWidth: '92%',
    background: tokens.surface.danger,
    color: tokens.text.danger,
    padding: '10px 14px',
    borderRadius: tokens.radius.md,
    fontSize: 13,
    lineHeight: 1.4,
    textAlign: 'center',
    border: `1px solid ${tokens.border.danger}`,
  },
  typing: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '1.45em',
  },
  attachmentGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    justifyContent: 'flex-end',
  },
  attachmentThumb: {
    width: 96,
    height: 96,
    objectFit: 'cover',
    display: 'block',
    borderRadius: tokens.radius.md,
  },
  attachmentDoc: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 200,
    padding: '6px 10px',
    borderRadius: tokens.radius.md,
    background: 'rgba(255, 255, 255, 0.18)',
    fontSize: tokens.font.size.sm,
    lineHeight: 1.3,
  },
  attachmentDocName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: tokens.font.weight.semibold,
  },
  attachmentText: {
    marginTop: 8,
  },
  retryBtn: {
    marginTop: tokens.space[2],
    border: `1px solid ${tokens.color.danger[300]}`,
    background: tokens.surface.elevated,
    color: tokens.color.danger[700],
    borderRadius: tokens.radius.sm,
    padding: '6px 12px',
    fontSize: tokens.font.size.sm,
    fontWeight: tokens.font.weight.semibold,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
} as const satisfies Record<string, CSSProperties>;

interface MessageListProps {
  messages: ChatMessage[];
  /** Non-null when the last send can be retried ('' for a files-only message). */
  lastFailedText: string | null;
  onRetry: () => void;
}

function SentAttachments({ attachments }: { attachments: SentAttachment[] }) {
  return (
    <div style={styles.attachmentGrid}>
      {attachments.map((a) =>
        a.kind === 'image' && a.previewUrl ? (
          <img
            key={a.path}
            src={a.previewUrl}
            alt={a.name}
            style={styles.attachmentThumb}
            draggable={false}
          />
        ) : (
          <div key={a.path} style={styles.attachmentDoc} title={a.name}>
            <span style={styles.attachmentDocName}>{a.name}</span>
            <span>PDF · {formatFileSize(a.size)}</span>
          </div>
        ),
      )}
    </div>
  );
}

export default function MessageList({
  messages,
  lastFailedText,
  onRetry,
}: MessageListProps) {
  const { t } = useTranslation();

  return (
    <>
      {messages.map((msg, index) => {
        const previous = messages[index - 1];
        const showTime = shouldShowMessageTime(msg, previous);
        const timeLabel =
          showTime && msg.createdAt != null
            ? formatMessageTime(
                msg.createdAt,
                i18n.language,
                t('chat.yesterday'),
              )
            : null;

        if (msg.sender === 'divider') {
          return <ConversationDivider key={msg.id} label={msg.text} />;
        }

        if (msg.sender === 'system' || msg.status === 'error') {
          return (
            <div key={msg.id} style={{ display: 'contents' }}>
              {timeLabel && <MessageTimestamp label={timeLabel} />}
              <div style={styles.bubbleSystem}>
                <div>{capitalizeErrorMessage(msg.text)}</div>
                {lastFailedText !== null && msg.status === 'error' && (
                  <button
                    type="button"
                    style={styles.retryBtn}
                    onClick={onRetry}
                  >
                    {t('chat.retry')}
                  </button>
                )}
              </div>
            </div>
          );
        }

        return (
          <div key={msg.id} style={{ display: 'contents' }}>
            {timeLabel && <MessageTimestamp label={timeLabel} />}
            <div
              style={
                msg.sender === 'user' ? styles.bubbleUser : styles.bubbleAvatar
              }
            >
              {msg.status === 'streaming' && !msg.text ? (
                <span style={styles.typing}>
                  <TypingIndicator label={t('chat.typing')} />
                </span>
              ) : msg.attachments?.length ? (
                <>
                  <SentAttachments attachments={msg.attachments} />
                  {msg.text && <div style={styles.attachmentText}>{msg.text}</div>}
                </>
              ) : (
                msg.text
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
